import { getCase } from '@/backend/case'
import { getQueryKey, useCaseSubscriptionStore } from '@/data/Cases/useCaseQuerySubscriptionStore'
import { serializeCase } from '@/modules/Cases/serializers'
import type { Case } from '@/modules/Cases/types'
import { ApiError } from '@/shared/utils/error'
import { invariant } from '@/shared/utils/typeAssertions'
import { useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/vue-query'
import { computed, onUnmounted, watch, type Ref } from 'vue'

export const useCaseQuery = (
  caseId: Ref<string | undefined | null>,
  workspaceId: Ref<string>,
  options: Partial<UseQueryOptions<QueryFnReturn, QueryError, QueryData>> = {},
) => {
  const queryKey = computed(() => getQueryKey(caseId.value))
  const query = useQuery<QueryFnReturn, QueryError, QueryData>({
    queryKey,
    staleTime: 5_000, // 5 seconds
    enabled: () => !!caseId.value,
    queryFn: async () => {
      invariant(caseId.value)
      const res = await getCase({ caseId: caseId.value, workspaceId: workspaceId.value })
      if (!res.ok) {
        throw ApiError.fromFailedResult(res)
      }

      return serializeCase(res.data, workspaceId.value)
    },
    ...options,
  })

  const client = useQueryClient()
  const store = useCaseSubscriptionStore()

  watch(
    caseId,
    (newCaseId, oldCaseId) => {
      if (oldCaseId) {
        store.removeInterest(oldCaseId)
      }

      if (!newCaseId) {
        return
      }

      store.addInterest(newCaseId, workspaceId.value)
    },
    {
      immediate: true,
    },
  )

  onUnmounted(() => {
    if (!caseId.value) return
    store.removeInterest(caseId.value)
  })

  const deleteCaseFile = (fileId: string) => {
    client.setQueryData(queryKey.value, (oldData: Case | undefined) => {
      if (!oldData) return

      const newCase = structuredClone(oldData)
      newCase.files = newCase.files.filter((f) => f.id !== fileId)
      return newCase
    })
  }

  const setCase = (newCase: Case) => {
    if (newCase.id !== caseId.value) {
      return
    }

    client.setQueryData(queryKey.value, newCase)
  }

  return { ...query, deleteCaseFile, setCase }
}

// What's returned by queryFm
type QueryFnReturn = Case

// The errors that could be thrown in queryFn
type QueryError = Error | ApiError

// The transformed data, returned by the select function
type QueryData = Case | undefined
